import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import Grid from '@mui/material/Grid2';
import { Box, Button, Paper } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { Waiting } from '../../../components/Waiting/Waiting';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { sharedStyles } from '../../../utils/Style';
import { getMenus } from '../../../api/Menu';
import { Menu } from '../../../interfaces/Menu/Menu';

const keys = [
  'storefront_navbar_text',
  'storefront_primary_menu',
  'storefront_category_menu',
  'storefront_footer_menu_one_title',
  'storefront_footer_menu_one',
  'storefront_footer_menu_two_title',
  'storefront_footer_menu_two',
];

export const MenuSettings = () => {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [settings, setSettings] = useState<Settings[]>([]);

  const [loadingMenus, setLoadingMenus] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const refreshSettings = () => {
    setLoadingSettings(true);
    getSettingsByType(SettingsType.FRONTEND)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingSettings(false));
  };

  useEffect(() => {
    refreshSettings();

    setLoadingMenus(true);
    getMenus()
      .then((m) => setMenus(m))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingMenus(false));
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.FRONTEND, key, settings, newValue),
      ),
    [settings],
  );

  const handleSubmit = () => {
    setLoadingSettings(true);
    upsertSettings(settings.filter((s) => keys.includes(s.key)))
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoadingSettings(false);
      });
  };

  const loading = loadingMenus || loadingSettings;

  return (
    <Grid container spacing={2}>
      <Waiting open={loading} />
      <Grid size={{ xs: 12, lg: 6 }}>
        <Paper
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            height: '95%',
          }}
        >
          <SettingsRow
            name='Menü Yazısı'
            localized
            valueType={SettingsValueType.TEXT}
            value={getSettingsValue(settings, 'storefront_navbar_text')}
            onChange={(newValue) =>
              handleChange('storefront_navbar_text', newValue)
            }
          />
          <SettingsRow
            name='Ana Menü'
            valueType={SettingsValueType.SELECT}
            value={getSettingsValue(settings, 'storefront_primary_menu')}
            numeric
            onChange={(newValue) =>
              handleChange('storefront_primary_menu', newValue)
            }
            options={menus.map((menu) => ({
              value: menu.id,
              label: menu.name,
            }))}
          />
          <SettingsRow
            name='Kategori Menüsü'
            valueType={SettingsValueType.SELECT}
            value={getSettingsValue(settings, 'storefront_category_menu')}
            numeric
            onChange={(newValue) =>
              handleChange('storefront_category_menu', newValue)
            }
            options={menus.map((menu) => ({
              value: menu.id,
              label: menu.name,
            }))}
          />
          <SettingsRow
            name='Footer 1 Başlığı'
            localized
            valueType={SettingsValueType.TEXT}
            value={getSettingsValue(
              settings,
              'storefront_footer_menu_one_title',
            )}
            onChange={(newValue) =>
              handleChange('storefront_footer_menu_one_title', newValue)
            }
          />
          <SettingsRow
            name='Footer 1'
            valueType={SettingsValueType.SELECT}
            value={getSettingsValue(settings, 'storefront_footer_menu_one')}
            numeric
            onChange={(newValue) =>
              handleChange('storefront_footer_menu_one', newValue)
            }
            options={menus.map((menu) => ({
              value: menu.id,
              label: menu.name,
            }))}
          />
          <SettingsRow
            name='Footer 2 Başlığı'
            localized
            valueType={SettingsValueType.TEXT}
            value={getSettingsValue(
              settings,
              'storefront_footer_menu_two_title',
            )}
            onChange={(newValue) =>
              handleChange('storefront_footer_menu_two_title', newValue)
            }
          />
          <SettingsRow
            name='Footer 2'
            valueType={SettingsValueType.SELECT}
            value={getSettingsValue(settings, 'storefront_footer_menu_two')}
            numeric
            onChange={(newValue) =>
              handleChange('storefront_footer_menu_two', newValue)
            }
            options={menus.map((menu) => ({
              value: menu.id,
              label: menu.name,
            }))}
          />
          <Box sx={{ display: 'flex', flexGrow: 1 }} />
          <Box display='flex' justifyContent='flex-end'>
            <Button
              variant='contained'
              onClick={handleSubmit}
              sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
            >
              Kaydet
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};
